import React, { useState, useMemo, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    FormControlLabel,
    Checkbox,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Grid
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { format } from 'date-fns';
import CollapsibleChartGroup from './CollapsibleChartGroup';
import { useStationsContext } from '../../context/StationsContext';

const MultiStationTrendChart = ({ data, loading, chartType, selectedStations, trendType }) => {
    const theme = useTheme();
    const { getStationName } = useStationsContext();
    const [selectedConfig, setSelectedConfig] = useState({});

    // Detect available stations from data keys or use prop
    const stations = useMemo(() => {
        const detectedStations = new Set();

        // 1. Add from Props (User Intent)
        if (selectedStations && selectedStations.length > 0) {
            selectedStations.forEach(s => detectedStations.add(s));
        }

        // 2. Add from Data (Actual Availability) - Robust scan of FIRST FEW records, not just [0]
        if (data && data.length > 0) {
            // Scan up to first 5 records to find keys, in case record 0 is incomplete
            const recordsToScan = data.slice(0, 5);
            recordsToScan.forEach(record => {
                Object.keys(record).forEach(k => {
                    if (k.includes('_')) {
                        const allBaseKeys = [
                            'at2mDegC', 'rh2mPer', 'rainFallMm', 'avgNetRadiationwBym2', 'ws2mMps', 'wd2mDeg', 'ap2mHpa',
                            'at4mDegC', 'rh4mPer', 'ws4mMps', 'wd4mDeg',
                            'at6mDegC', 'rh6mPer', 'ws6mMps', 'wd6mDeg'
                        ];
                        for (const baseKey of allBaseKeys) {
                            if (k.endsWith(`_${baseKey}`)) {
                                const stationName = k.replace(`_${baseKey}`, '');
                                detectedStations.add(stationName);
                                break;
                            }
                        }
                    }
                });
            });
        }

        return Array.from(detectedStations).sort();
    }, [data, selectedStations]);

    // Initialize selection state - Enable ALL by default
    useEffect(() => {
        if (stations.length > 0) {
            const initialConfig = {};
            stations.forEach(station => {
                initialConfig[station] = {
                    selected: true // Force select all stations
                };
            });
            setSelectedConfig(initialConfig);
        }
    }, [stations.join(',')]); // Depend on joined string to catch content changes

    const handleStationToggle = (station) => {
        setSelectedConfig(prev => ({
            ...prev,
            [station]: {
                ...prev[station],
                selected: !prev[station]?.selected
            }
        }));
    };

    // Distinct dash patterns for up to 10 stations
    const DASH_PATTERNS = [
        "",                 // 1. Solid (Station 1)
        "10 5",            // 2. Long Dash
        "2 2",             // 3. Dots
        "10 2 2 2",        // 4. Dash Dot
        "5 5",             // 5. Medium Dash
        "20 5",            // 6. Extra Long Dash
        "10 5 2 5 2 5",    // 7. Dash Dot Dot
        "5 2 5 2",         // 8. Short Double Dash
        "2 5",             // 9. Sparse Dots
        "15 3 3 3"         // 10. Complex Pattern
    ];

    // Helper to generate dynamic parameters for a group
    const generateGroupParams = (baseParams) => {
        const params = [];
        stations.forEach((station, idx) => {
            // Check if station is selected in our local config
            if (selectedConfig[station]?.selected) {
                baseParams.forEach(bp => {
                    const dashPattern = DASH_PATTERNS[idx % DASH_PATTERNS.length];
                    const isVisible = bp.defaultOn !== undefined ? bp.defaultOn : true; // defaultOn from properties
                    const stationDisplayName = getStationName(station) || station;

                    params.push({
                        key: `${station}_${bp.key}`,
                        label: `${stationDisplayName} - ${bp.label}`,
                        shortLabel: bp.shortLabel || bp.label,
                        color: bp.color,
                        strokeWidth: bp.strokeWidth || 2,
                        strokeDasharray: dashPattern,
                        defaultOn: isVisible
                    });
                });
            }
        });
        return params;
    };

    // Helper to generate station groups for a chart
    const generateStationGroups = (baseParams) => {
        const groups = [];
        stations.forEach(station => {
            if (selectedConfig[station]?.selected) {
                const keys = baseParams.map(bp => `${station}_${bp.key}`);
                const stationDisplayName = getStationName(station) || station;
                groups.push({
                    label: stationDisplayName,
                    paramKeys: keys
                });
            }
        });
        return groups;
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                <Typography>Loading multi-station data...</Typography>
            </Box>
        );
    }

    if (!data || data.length === 0) {
        return <Box sx={{ p: 5, textAlign: 'center' }}>No data available.</Box>;
    }

    // Limit table data to 100 records
    const tableData = data.slice(0, 100);

    // Base Definitions (Exact copy of single station capabilities)
    const baseParams2m = [
        { key: 'at2mDegC', label: 'Temperature (°C)', color: '#c30a0aff', strokeWidth: 3, defaultOn: true },
        { key: 'rh2mPer', label: 'Humidity (%)', color: '#1565C0', strokeWidth: 3, defaultOn: true },
        { key: 'rainFallMm', label: 'Rainfall (mm)', color: '#311B92', strokeWidth: 3, defaultOn: true },
        { key: 'avgNetRadiationwBym2', label: 'Net Rad (W/m²)', color: '#eee30dff', strokeWidth: 3, defaultOn: true },
        { key: 'ws2mMps', label: 'Wind Spd (m/s)', color: '#2E7D32', strokeWidth: 3, defaultOn: true },
        { key: 'wd2mDeg', label: 'Wind Dir (°)', color: '#00897B', strokeWidth: 3, defaultOn: true },
        { key: 'ap2mHpa', label: 'Pressure (hPa)', color: '#7B1FA2', strokeWidth: 3, defaultOn: true }
    ];

    const baseParams4m = [
        { key: 'at4mDegC', label: 'Temperature (°C)', color: '#c30a0aff', strokeWidth: 3, defaultOn: true },
        { key: 'rh4mPer', label: 'Humidity (%)', color: '#1565C0', strokeWidth: 3, defaultOn: true },
        { key: 'rainFallMm', label: 'Rainfall (mm)', color: '#311B92', strokeWidth: 3, defaultOn: true },
        { key: 'avgNetRadiationwBym2', label: 'Net Rad (W/m²)', color: '#eee30dff', strokeWidth: 3, defaultOn: true },
        { key: 'ws4mMps', label: 'Wind Spd (m/s)', color: '#2E7D32', strokeWidth: 3, defaultOn: true },
        { key: 'wd4mDeg', label: 'Wind Dir (°)', color: '#00897B', strokeWidth: 3, defaultOn: true },
    ];

    const baseParams6m = [
        { key: 'at6mDegC', label: 'Temperature (°C)', color: '#c30a0aff', strokeWidth: 3, defaultOn: true },
        { key: 'rh6mPer', label: 'Humidity (%)', color: '#1565C0', strokeWidth: 3, defaultOn: true },
        { key: 'rainFallMm', label: 'Rainfall (mm)', color: '#311B92', strokeWidth: 3, defaultOn: true },
        { key: 'avgNetRadiationwBym2', label: 'Net Rad (W/m²)', color: '#eee30dff', strokeWidth: 3, defaultOn: true },
        { key: 'ws6mMps', label: 'Wind Spd (m/s)', color: '#2E7D32', strokeWidth: 3, defaultOn: true },
        { key: 'wd6mDeg', label: 'Wind Dir (°)', color: '#00897B', strokeWidth: 3, defaultOn: true },
    ];
    const baseParamsCombined = [
        // temperature
        { key: 'at2mDegC', label: 'Temp (2m) °C', color: '#e53935', strokeWidth: 3, defaultOn: true },
        { key: 'at4mDegC', label: 'Temp (4m) °C', color: '#c30a0aff', strokeWidth: 3, defaultOn: true },
        { key: 'at6mDegC', label: 'Temp (6m) °C', color: '#7f0000', strokeWidth: 3, defaultOn: true },
        //Humidity
        { key: 'rh2mPer', label: 'Hum (2m) %', color: '#1E88E5', strokeWidth: 3, defaultOn: true },
        { key: 'rh4mPer', label: 'Hum (4m) %', color: '#1565C0', strokeWidth: 3, defaultOn: true },
        { key: 'rh6mPer', label: 'Hum (6m) %', color: '#0D47A1', strokeWidth: 3, defaultOn: true },
        { key: 'rainFallMm', label: 'Rain (2m) mm', color: '#311B92', strokeWidth: 3, defaultOn: true },
        { key: 'avgNetRadiationwBym2', label: 'Net Rad (2m)', color: '#eee30dff', strokeWidth: 3, defaultOn: true },
        //wind speed
        { key: 'ws2mMps', label: 'Wind Spd (2m)', color: '#43A047', strokeWidth: 3, defaultOn: true },
        { key: 'ws4mMps', label: 'Wind Spd (4m)', color: '#2E7D32', strokeWidth: 3, defaultOn: true },
        { key: 'ws6mMps', label: 'Wind Spd (6m)', color: '#106314ff', strokeWidth: 3, defaultOn: true },
        // wind direction
        { key: 'wd2mDeg', label: 'Wind Dir (2m)', color: '#4DB6AC', strokeWidth: 3, defaultOn: true }, // light teal
        { key: 'wd4mDeg', label: 'Wind Dir (4m)', color: '#00897B', strokeWidth: 3, defaultOn: true }, // base teal
        { key: 'wd6mDeg', label: 'Wind Dir (6m)', color: '#004D40', strokeWidth: 3, defaultOn: true }, // dark teal
        { key: 'ap2mHpa', label: 'Pressure (2m)', color: '#7B1FA2', strokeWidth: 3, defaultOn: true },
    ];

    const multiParams2m = generateGroupParams(baseParams2m);
    const multiParams4m = generateGroupParams(baseParams4m);
    const multiParams6m = generateGroupParams(baseParams6m);
    const multiParamsCombined = generateGroupParams(baseParamsCombined);

    // Create a key string based on selected stations to force remount of chart groups when selection changes
    const selectionKey = stations.filter(s => selectedConfig[s]?.selected).join(',');

    return (
        <Box sx={{ width: '100%', p: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 2 }}>
                <CollapsibleChartGroup
                    key={`2m-${selectionKey}`}
                    title="Multi-Station Trends – 2m Depth"
                    icon="📈"
                    parameters={multiParams2m}
                    paramGroups={generateStationGroups(baseParams2m)}
                    data={data}
                    defaultExpanded={true}
                    gradientColors={[theme.palette.primary.main, theme.palette.primary.light]}
                    topBorderColor={theme.palette.primary.main}
                    minChartWidth={3000}
                    chartType={chartType}
                    trendType={trendType}
                />

                <CollapsibleChartGroup
                    key={`4m-${selectionKey}`}
                    title="Multi-Station Trends – 4m Depth"
                    icon="📈"
                    parameters={multiParams4m}
                    paramGroups={generateStationGroups(baseParams4m)}
                    data={data}
                    defaultExpanded={true}
                    gradientColors={[theme.palette.secondary.main, theme.palette.secondary.light]}
                    topBorderColor={theme.palette.secondary.main}
                    minChartWidth={3000}
                    chartType={chartType}
                    trendType={trendType}
                />

                <CollapsibleChartGroup
                    key={`6m-${selectionKey}`}
                    title="Multi-Station Trends – 6m Depth"
                    icon="📈"
                    parameters={multiParams6m}
                    paramGroups={generateStationGroups(baseParams6m)}
                    data={data}
                    defaultExpanded={true}
                    gradientColors={['#01383eff', '#015c66']}
                    topBorderColor="#01383eff"
                    minChartWidth={3000}
                    chartType={chartType}
                    trendType={trendType}
                />

                <CollapsibleChartGroup
                    key={`combined-${selectionKey}`}
                    title="Combined Depth Trends (2m, 4m, 6m)"
                    icon="📊"
                    parameters={multiParamsCombined}
                    paramGroups={generateStationGroups(baseParamsCombined)}
                    data={data}
                    defaultExpanded={true}
                    gradientColors={[theme.palette.info.main, theme.palette.info.light]}
                    topBorderColor={theme.palette.info.main}
                    minChartWidth={3000}
                    chartType={chartType}
                    trendType={trendType}
                />
            </Box>
        </Box>
    );
};

export default MultiStationTrendChart;
